import { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import './SettingsAdmin.css';
import { SettingsApi } from '../../api/settingsApi';
import { useAdminAccess } from '../../context/AdminAccessContext';

const SettingsAdmin = () => {
  const { adminSettings, refreshSecureSettings, applySecureSettings } = useAdminAccess();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState('success');
  const [form, setForm] = useState({
    enabled: false,
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let canceled = false;

    const bootstrap = async () => {
      if (!adminSettings) {
        try {
          await refreshSecureSettings();
        } catch (_) {
          // fallthrough
        }
      }
      if (!canceled) {
        setLoading(false);
      }
    };

    bootstrap();

    return () => {
      canceled = true;
    };
  }, [adminSettings, refreshSecureSettings]);

  useEffect(() => {
    if (adminSettings) {
      setForm((prev) => ({
        ...prev,
        enabled: adminSettings.enabled,
        username: adminSettings.username || ''
      }));
    }
  }, [adminSettings]);

  const updateField = (key) => (event) => {
    const value = key === 'enabled' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    if (form.password && form.password !== form.confirmPassword) {
      setErrors({ password: ['Passwords do not match.'] });
      setFeedback('Passwords do not match.');
      setFeedbackType('error');
      return;
    }

    setSaving(true);
    setFeedback('');
    setFeedbackType('success');
    setErrors({});

    const payload = {
      enabled: form.enabled,
      username: form.username.trim(),
    };

    if (form.password) {
      payload.password = form.password;
    }

    try {
      const data = await SettingsApi.updateAdminSettings(payload);
      applySecureSettings(data);
      setFeedback('Settings saved.');
      setFeedbackType('success');
      setForm((prev) => ({ ...prev, password: '', confirmPassword: '' }));
    } catch (apiError) {
      const responseErrors = apiError?.response?.data?.details || {};
      setErrors(responseErrors);
      const message = apiError?.response?.data?.message || 'Unable to save settings.';
      setFeedback(message);
      setFeedbackType('error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Settings" description="Control access to the admin console.">
        <div className="admin-settings-loading">Loading settings…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Settings" description="Control access to the admin console.">
      <form className="admin-settings-form" onSubmit={handleSubmit}>
        {feedback && (
          <div className={`admin-settings-feedback ${feedbackType === 'error' ? 'error' : ''}`}>
            {feedback}
          </div>
        )}

        <label className="admin-settings-toggle">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={updateField('enabled')}
            disabled={loading || saving}
          />
          <div>
            <strong>Enable admin console</strong>
            <p>Turn off to completely hide the /admin routes.</p>
          </div>
        </label>

        <div className="admin-settings-field">
          <label>Admin username</label>
          <input
            type="text"
            value={form.username}
            onChange={updateField('username')}
            disabled={loading || saving}
            required
          />
          {Array.isArray(errors.username) && errors.username[0] && (
            <small>{errors.username[0]}</small>
          )}
        </div>

        <div className="admin-settings-grid">
          <div className="admin-settings-field">
            <label>New password</label>
            <input
              type="password"
              value={form.password}
              onChange={updateField('password')}
              disabled={loading || saving}
              placeholder="Leave blank to keep current"
            />
            {Array.isArray(errors.password) && errors.password[0] && (
              <small>{errors.password[0]}</small>
            )}
          </div>
          <div className="admin-settings-field">
            <label>Confirm password</label>
            <input
              type="password"
              value={form.confirmPassword}
              onChange={updateField('confirmPassword')}
              disabled={loading || saving}
            />
          </div>
        </div>

        <div className="admin-settings-actions">
          <button type="submit" className="admin-primary-btn" disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
};

export default SettingsAdmin;
